import {
  Application,
  Graphics,
  Text,
  Sprite,
  RenderTexture,
  settings,
  Texture,
} from "pixi.js";
import { Viewport } from "pixi-viewport";
import { Vector2, EventDispatcher } from "three";
import {
  EVENT_NEW,
  EVENT_DELETED,
  EVENT_LOADED,
  EVENT_SELECTED,
  EVENT_ROOM_DIMENSION_CLICKED,
  EVENT_WALL_DIMENSION_CLICKED,
  EVENT_NEW_ROOMS_ADDED,
  EVENT_KEY_RELEASED,
  EVENT_KEY_PRESSED,
  EVENT_WALL_CLICKED,
  EVENT_CORNER_CLICKED,
  EVENT_ROOM_CLICKED,
  EVENT_NOTHING_SELECTED,
  EVENT_MOVED,
  EVENT_MODE_RESET,
  EVENT_EXTERNAL_FLOORPLAN_LOADED,
  EVENT_ICONS_CLICKED,
  EVENT_LABEL_CLICKED,
  EVENT_ARROW_CLICKED,
  EVENT_SWITCH_MODE,
  EVENT_TRANSLATE_CLICKED,
} from "../core/events";
import { Grid } from "./Grid";
import { CornerView } from "./CornerView";
import { WallView, WallDimensions } from "./WallView";
import { RoomDimensions, RoomTransformControl, RoomView } from "./RoomView";
import { Dimensioning } from "../core/dimensioning";
import { KeyboardListener } from "./KeyboardManager";
import {
  Configuration,
  snapToGrid,
  snapTolerance,
  viewBounds,
  fontSize,
  currentFontSize,
} from "../core/configuration";

import { IS_TOUCH_DEVICE } from "../../DeviceInfo";
import { CornerGroupTransform } from "./CornerGroupTransform";
import Room from "../model/room";
import { BoundaryView } from "./BoundaryView";

import { Icons } from "./Icons";
import { Labels } from "./Labels";
import { ArrowView } from "./ArrowView";
// import * as target from './target.png';
import * as arrowhead from "../../assets/arrow-up.svg";

export const floorplannerModes = { MOVE: 0, DRAW: 1, EDIT_ISLANDS: 2, DRAG: 3 };

const fontSizeList = {
  0.1: 100,
  0.2: 96,
  0.3: 88,
  0.4: 84,
  0.5: 72,
  0.6: 64,
  0.7: 56,
  0.8: 48,
  0.9: 36,
  "1.0": 30,
};

class TemporaryIcon extends Graphics {
  constructor() {
    super();
    this.__iconSprite = null;
    this.interactive = true;
    this.buttonMode = true;
  }

  __update(iconUrl, location) {
    if (!this.__iconSprite) {
      const texture = Texture.from(iconUrl.url);
      this.__iconSprite = new Sprite(texture);
      this.__iconSprite.anchor.set(0.5, 0.5);
      this.__iconSprite.pivot.set(0.5, 0.5);
      this.__iconSprite.position = location;
      this.__iconSprite.interactive = true;
      this.addChild(this.__iconSprite);
    } else {
      this.__iconSprite.position = location;
    }
  }
}

class TemporaryWall extends Graphics {
  constructor() {
    super();
    this.__fontSize = 18;
    this.__textfield = new Text("Length: ", {
      fontFamily: "Arial",
      fontSize: this.__fontSize,
      fill: "black",
      align: "center",
    });
    this.__textfield.resolution = 2;
    this.addChild(this.__textfield);
  }

  __toPixels(vector) {
    vector.x = Dimensioning.cmToPixel(vector.x);
    vector.y = Dimensioning.cmToPixel(vector.y);
    return vector;
  }

  set fontSize(value) {
    this.__fontSize = value;
    this.__textfield.style.fontSize = value;
    Configuration.setValue(currentFontSize, value);
  }

  get fontSize() {
    return this.__fontSize;
  }

  update(corner, endPoint, startPoint) {
    this.clear();
    this.__textfield.visible = false;
    if (corner !== undefined && endPoint !== undefined) {
      let pxCornerCo = this.__toPixels(corner.location.clone());
      let pxEndPoint = this.__toPixels(endPoint.clone());

      var dx = pxEndPoint.x - pxCornerCo.x;
      var dy = pxEndPoint.y - pxCornerCo.y;
      var angle = Math.atan2(dy, dx);

      let vect = endPoint.clone().sub(corner.location);
      let midPoint = pxEndPoint
        .clone()
        .sub(pxCornerCo)
        .multiplyScalar(0.5)
        .add(pxCornerCo);

      this.lineStyle(5, 0x008cba);
      this.moveTo(pxCornerCo.x, pxCornerCo.y);
      this.lineTo(pxEndPoint.x, pxEndPoint.y);

      this.beginFill(0x008cba, 0.5);

      this.drawCircle(pxEndPoint.x, pxEndPoint.y, 5);
      // this.endFill();
      // this.lineStyle(2, 0x008CBA,0.6);
      // this.arc(pxCornerCo.x,pxCornerCo.y,20,0,angle,false);

      this.__textfield.position.x = midPoint.x;
      this.__textfield.position.y = midPoint.y;
      this.__textfield.text = Dimensioning.cmToMeasure(vect.length());
      this.__textfield.visible = true;
    }
    if (startPoint !== undefined) {
      let pxStartCo = this.__toPixels(startPoint);
      // let pxStartCo = startPoint;
      this.beginFill(0x008cba, 0.5);
      this.drawCircle(pxStartCo.x, pxStartCo.y, 5);
    }
  }
}

class TemporaryArrow extends Graphics {
  constructor() {
    super();
    this.__arrowHeadSprite = null;
  }

  __toPixels(vector) {
    vector.x = Dimensioning.cmToPixel(vector.x);
    vector.y = Dimensioning.cmToPixel(vector.y);
    return vector;
  }

  arrowShape(fromx, fromy, tox, toy) {
    var headlen = 24; // length of head in pixels
    var dx = tox - fromx;
    var dy = toy - fromy;
    var angle = Math.atan2(dy, dx);
    this.clear();
    this.lineStyle(5, 0x000000);
    this.moveTo(fromx, fromy);
    this.lineTo(tox, toy);
    this.lineTo(
      tox - headlen * Math.cos(angle - Math.PI / 6),
      toy - headlen * Math.sin(angle - Math.PI / 6),
    );
    this.moveTo(tox, toy);
    this.lineTo(
      tox - headlen * Math.cos(angle + Math.PI / 6),
      toy - headlen * Math.sin(angle + Math.PI / 6),
    );

    this.lineStyle(5, 0x000000);
    this.beginFill(0x000000, alpha);
    this.moveTo(fromx, fromy);
    this.lineTo(tox, toy);
    this.lineTo(
      tox - headlen * Math.cos(angle - Math.PI / 6),
      toy - headlen * Math.sin(angle - Math.PI / 6),
    );
    this.moveTo(tox, toy);
    this.lineTo(
      tox - headlen * Math.cos(angle + Math.PI / 6),
      toy - headlen * Math.sin(angle + Math.PI / 6),
    );

    this.endFill();
  }

  drawArrow(fromx, fromy, tox, toy, arrowWidth, color) {
    //variables to be used when creating the arrow
    var headlen = 16;
    var angle = Math.atan2(toy - fromy, tox - fromx);

    this.clear();

    //starting path of the arrow from the start square to the end square
    //and drawing the stroke
    this.lineStyle(5, color, 1.0);
    this.beginFill(color, 1.0);
    this.moveTo(fromx, fromy);
    this.lineTo(tox, toy);

    //starting a new path from the head of the arrow to one of the sides of
    //the point
    this.lineStyle(5, color, 1.0);
    this.moveTo(tox, toy);
    this.lineTo(
      tox - headlen * Math.cos(angle - Math.PI / 7),
      toy - headlen * Math.sin(angle - Math.PI / 7),
    );

    //path from the side point of the arrow, to the other side point
    this.lineTo(
      tox - headlen * Math.cos(angle + Math.PI / 7),
      toy - headlen * Math.sin(angle + Math.PI / 7),
    );

    //path from the side point back to the tip of the arrow, and then
    //again to the opposite side point
    this.lineTo(tox, toy);
    this.lineTo(
      tox - headlen * Math.cos(angle - Math.PI / 7),
      toy - headlen * Math.sin(angle - Math.PI / 7),
    );

    //draws the paths created above
    this.endFill();
  }

  update(corner, endPoint, startPoint) {
    this.clear();
    let arrowHead = 24;

    if (corner !== undefined && endPoint !== undefined) {
      let pxCornerCo = corner;
      let pxEndPoint = endPoint;

      this.drawArrow(
        pxCornerCo.x,
        pxCornerCo.y,
        pxEndPoint.x,
        pxEndPoint.y,
        10,
        "0x000000",
      );

      // this.lineStyle(5, 0x000000);
      // this.moveTo(pxCornerCo.x, pxCornerCo.y);
      // this.lineTo(pxEndPoint.x, pxEndPoint.y);

      // this.beginFill(0x000000, 0.4);
      // this.drawRect(pxEndPoint.x - arrowHead/2, pxEndPoint.y - arrowHead/2, arrowHead,arrowHead);
    }
    if (startPoint !== undefined) {
      let pxStartCo = startPoint;
      this.beginFill(0x000000, 0.5);
      this.drawCircle(pxStartCo.x, pxStartCo.y, 5);
    }
  }
}

export class Viewer extends Application {
  constructor(canvasHolder, floorplan, options) {
    const { pixiAppOptions, pixiViewportOptions } = options;
    const pixiDefalultAppOpts = {
      width: 512,
      height: 512,
      resolution: window.devicePixelRatio || 2,
      preserveDrawingBuffer: true,
      antialias: true,
      autoDensity: true,
      backgroundAlpha: 0,
    };
    super(Object.assign(pixiDefalultAppOpts, pixiAppOptions));
    this.__eventDispatcher = new EventDispatcher();

    let opts = {
      "corner-radius": 7,
      "boundary-point-radius": 3.0,
      "boundary-line-thickness": 2.0,
      "boundary-point-color": "#030303",
      "boundary-line-color": "#090909",
      pannable: true,
      zoomable: true,
      dimlinecolor: "#3EDEDE",
      dimarrowcolor: "#000000",
      dimtextcolor: "#000000",
      scale: true,
      rotate: true,
      translate: true,
      resize: true,
    };

    for (var opt in opts) {
      if (opts.hasOwnProperty(opt) && options.hasOwnProperty(opt)) {
        opts[opt] = options[opt];
      }
    }

    this.__mode = floorplannerModes.MOVE;
    this.__canvasHolder = document.getElementById(canvasHolder);
    this.__floorplan = floorplan;
    this.__options = opts;

    this.__lastNode = null;
    this.__tempWall = new TemporaryWall();
    this.__tempArrow = new TemporaryArrow();
    // this.__tempIcon = new TemporaryIcon();

    this.__corners2d = [];
    this.__walls2d = [];
    this.__rooms2d = [];
    this.__arrow2d = [];
    this.__icon2d = [];
    this.__label2d = [];
    this.__roomDiemensionField2d = [];
    this.__wallDiemensionField2d = [];
    this.__entities2D = [];

    this.__drawArrow = false;
    this.__drawIcon = false;
    this.__drawLabel = false;
    this.__iconData = null;
    this.__activeSprit = null;
    this.__externalCorners2d = [];
    this.__externalWalls2d = [];
    this.__externalRooms2d = [];
    this.__externalEntities2d = [];

    this.__arrowPoints = [];

    this.__worldWidth = 3000;
    this.__worldHeight = 3000;
    this.__currentWidth = 500;
    this.__currentHeight = 500;
    this.__currentSelection = null;

    this.__zoomedEvent = this.__zoomed.bind(this);
    this.__pannedEvent = this.__panned.bind(this);
    this.__selectionMonitorEvent = this.__selectionMonitor.bind(this);
    this.__cornerMovedEvent = this.__cornerMoved.bind(this);

    this.__drawModeMouseDownEvent = this.__drawModeMouseDown.bind(this);
    this.__drawModeMouseUpEvent = this.__drawModeMouseUp.bind(this);
    this.__drawModeMouseMoveEvent = this.__drawModeMouseMove.bind(this);

    this.__redrawFloorplanEvent = this.__redrawFloorplan.bind(this);
    this.__drawExternalFloorplanEvent = this.__drawExternalFloorplan.bind(this);
    this.__windowResizeEvent = this._handleWindowResize.bind(this);
    this.__resetFloorplanEvent = this.__resetFloorplan.bind(this);

    this.__floorplanLoadedEvent = this.__center.bind(this);

    const pixiViewportDefaultOpts = {
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      worldWidth: this.__worldWidth,
      worldHeight: this.__worldHeight,
      interaction: this.renderer.plugins.interaction,
      passiveWheel: false,
    };

    this.__floorplanContainer = new Viewport(
      Object.assign(pixiViewportDefaultOpts, pixiViewportOptions),
    );
    this.__tempWallHolder = new Graphics();
    this.__exportCanvasHolder = new Graphics();

    this.__snapToGrid = false;
    this.__keyboard = new KeyboardListener();
    this.__keyListenerEvent = this.__keyListener.bind(this);

    let origin = new Graphics();
    this.__floorplanElementsHolder = new Graphics();

    this.__boundaryHolder = new Graphics();
    this.__grid2d = new Grid(this.view, options);
    this.__boundaryRegion2D = null;
    this.__groupTransformer = new CornerGroupTransform(
      this.__floorplan,
      this.__options,
    );
    this.__groupTransformer.visible = false;
    this.__groupTransformer.selected = null;

    // origin.beginFill(0xFF0000);
    // origin.drawCircle(0, 0, 5);

    this.__floorplanContainer.position.set(
      window.innerWidth * 0.5,
      window.innerHeight * 0.5,
    );

    // this.renderer.backgroundColor = 0xFFFFFF;
    this.renderer.autoResize = true;
    this.renderer.roundPixels = true;

    this.__tempWall.visible = false;
    this.__tempArrow.visible = false;

    this.__floorplanContainer.addChild(this.__grid2d);
    this.__floorplanContainer.addChild(this.__boundaryHolder);
    this.__floorplanContainer.addChild(this.__floorplanElementsHolder);
    this.__floorplanContainer.addChild(this.__groupTransformer);

    this.__tempWallHolder.addChild(this.__tempWall);

    this.__tempWallHolder.addChild(this.__tempArrow);

    this.stage.addChild(this.__floorplanContainer);
    this.stage.addChild(this.__tempWallHolder);

    this.view.classList.add("2dCanvas");
    this.__canvasHolder.appendChild(this.view);

    this.__floorplanContainer.drag().pinch().wheel();

    if (!this.__options.pannable) {
      this.__floorplanContainer.plugins.pause("drag");
    }

    if (!this.__options.zoomable) {
      this.__floorplanContainer.plugins.pause("wheel");
      this.__floorplanContainer.plugins.pause("pinch");
    }

    this.__keyboard.addEventListener(
      EVENT_KEY_RELEASED,
      this.__keyListenerEvent,
    );
    this.__keyboard.addEventListener(
      EVENT_KEY_PRESSED,
      this.__keyListenerEvent,
    );

    this.__floorplanContainer.on("zoomed", this.__zoomedEvent);
    this.__floorplanContainer.on("moved", this.__pannedEvent);
    this.__floorplanContainer.on("clicked", this.__selectionMonitorEvent);

    this.__floorplanContainer.on("mousedown", this.__drawModeMouseDownEvent);
    this.__floorplanContainer.on("mouseup", this.__drawModeMouseUpEvent);
    this.__floorplanContainer.on("mousemove", this.__drawModeMouseMoveEvent);

    //User touches the screen then emulate the Mouseup event creating a corner
    this.__floorplanContainer.on("touchstart", this.__drawModeMouseUpEvent);
    //User then touch moves and lifts the finger away from the screen. Now create the next corner
    this.__floorplanContainer.on("touchend", this.__drawModeMouseUpEvent);

    //Use touches and drags across the screen then emulate drawing the temporary wall
    this.__floorplanContainer.on("touchmove", this.__drawModeMouseMoveEvent);

    // this.__floorplan.addEventListener(EVENT_UPDATED, (evt) => scope.__redrawFloorplan(evt));

    this.__floorplan.addEventListener(
      EVENT_LOADED,
      this.__floorplanLoadedEvent,
    );

    this.__floorplan.addEventListener(
      EVENT_MODE_RESET,
      this.__resetFloorplanEvent,
    );
    this.__floorplan.addEventListener(EVENT_NEW, this.__redrawFloorplanEvent);
    this.__floorplan.addEventListener(
      EVENT_DELETED,
      this.__redrawFloorplanEvent,
    );

    this.__floorplan.addEventListener(
      EVENT_NEW_ROOMS_ADDED,
      this.__redrawFloorplanEvent,
    );

    this.__floorplan.addEventListener(
      EVENT_EXTERNAL_FLOORPLAN_LOADED,
      this.__drawExternalFloorplanEvent,
    );

    window.addEventListener("resize", this.__windowResizeEvent);
    window.addEventListener("orientationchange", this.__windowResizeEvent);

    this._handleWindowResize();

    this.__center();
  }

  __drawBoundary() {
    // return;
    if (this.__boundaryRegion2D) {
      this.__boundaryRegion2D.remove();
    }

    if (this.__floorplan.boundary) {
      if (this.__floorplan.boundary.isValid) {
        this.__boundaryRegion2D = new BoundaryView(
          this.__floorplan,
          this.__options,
          this.__floorplan.boundary,
        );
        this.__boundaryHolder.addChild(this.__boundaryRegion2D);
      }
    }
  }

  __keyListener(evt) {
    if (evt.type === EVENT_KEY_PRESSED && evt.key === "Shift") {
      this.__snapToGrid = true;
    }
    if (evt.type === EVENT_KEY_RELEASED && evt.key === "Shift") {
      this.__snapToGrid = false;
    }
    if (evt.key === "Escape") {
      this.switchMode(floorplannerModes.MOVE);
    }
  }

  getImageData() {
    let imageData;
    this.__floorplanElementsHolder.getLocalBounds();
    imageData = this.renderer.plugins.extract.base64(
      this.__floorplanElementsHolder,
      "image/png",
      1,
    );
    return { image: imageData };
  }

  getImageData1() {
    let imageData;

    imageData = this.renderer.plugins.extract.base64(this.__floorplanContainer);
    // imageData = this.renderer.plugins.extract.base64(this.__floorplanElementsHolder);
    return { image: imageData };
  }

  insertIcons(icon) {
    if (icon) {
      this.__drawIcon = true;
      this.__iconData = this.__floorplan.newIcon(icon);
    }
  }

  insertLabels(label) {
    if (label) {
      this.__drawLabel = true;
      this.__iconData = this.__floorplan.newLabel(label);
    }
  }

  switchMode(mode) {
    if (
      this.__mode === floorplannerModes.EDIT_ISLANDS &&
      mode !== floorplannerModes.EDIT_ISLANDS
    ) {
      this.__floorplan.update();
    }
    // this.__eventDispatcher.dispatchEvent({ type: EVENT_SWITCH_MODE, item: mode });
    switch (mode) {
      case floorplannerModes.DRAW:
        this.__mode = floorplannerModes.DRAW;
        this.__floorplanContainer.plugins.pause("drag");
        for (let i = 0; i < this.__entities2D.length; i++) {
          this.__entities2D[i].interactive = false;
        }
        this.__changeCursorMode();
        if (!this.__drawArrow) {
          this.__tempWall.update();
          this.__tempWall.visible = true;
        } else {
          this.__tempArrow.update();
          this.__tempArrow.visible = true;
        }

        this.__groupTransformer.visible = false;
        this.__groupTransformer.selected = null;
        break;
      case floorplannerModes.EDIT_ISLANDS:
        this.__mode = floorplannerModes.EDIT_ISLANDS;
        if (this.__currentSelection instanceof Room) {
          this.__groupTransformer.visible = true;
          this.__groupTransformer.selected = this.__currentSelection;
        } else {
          this.__groupTransformer.visible = false;
          this.__groupTransformer.selected = null;
        }

        this.__floorplanContainer.plugins.pause("drag");
        for (let i = 0; i < this.__corners2d.length; i++) {
          this.__corners2d[i].interactive = false;
        }
        for (let i = 0; i < this.__walls2d.length; i++) {
          this.__walls2d[i].interactive = false;
        }
        this.__changeCursorMode();
        break;
      case floorplannerModes.MOVE:
        this.__mode = floorplannerModes.MOVE;
        for (let i = 0; i < this.__entities2D.length; i++) {
          this.__entities2D[i].interactive = true;
        }
        this.__tempWall.visible = false;
        this.__tempArrow.visible = false;
        this.__groupTransformer.visible = false;
        this.__groupTransformer.selected = null;
        this.__lastNode = null;
        this.__floorplanContainer.plugins.resume("drag");
        this.__changeCursorMode();
        break;
      default:
        throw new Error("Unknown Viewer2D mode");
    }
  }

  getCursorPointer() {
    switch (this.__mode) {
      case floorplannerModes.DRAW:
        return "crosshair";
      case floorplannerModes.EDIT_ISLANDS:
        return "grabbing";
      case floorplannerModes.MOVE:
        return "default";
      default:
        return "default";
    }
  }

  __changeCursorMode() {
    let cursor = this.getCursorPointer();
    this.renderer.plugins.interaction.cursorStyles.crosshair = cursor;
    this.renderer.plugins.interaction.cursorStyles.default = cursor;
    this.renderer.plugins.interaction.setCursorMode(cursor);
  }

  __drawModeMouseDown(evt) {
    if (IS_TOUCH_DEVICE) {
      this.__drawModeMouseUp(evt);
    }
  }

  __getNewRoomData() {
    return this.__rooms2d.find((room) => room.room.code === "none");
  }

  setFontSize(fontsize) {
    let roomDiemensionField2d = this.__roomDiemensionField2d;
    let wallDiemensionField2d = this.__wallDiemensionField2d;
    let lineHeight = fontsize < 36 ? fontsize * 1 : fontsize * 0.8;
    roomDiemensionField2d.forEach((field) => {
      field.__roomAreaField.style.fontSize = fontsize;
      field.__roomAreaField.style.lineHeight = lineHeight;
      field.drawOutlineBox();
    });
    wallDiemensionField2d.forEach((field) => {
      field.__textfield.style.fontSize = fontsize;
      field.__textfield.style.lineHeight = lineHeight;
      field.drawOutlineBox();
    });
  }

  setLastAction() {
    let lastCorner = this.__floorplan.undoLastActionPoint();
    this.__lastNode = lastCorner.end;
  }

  __drawModeMouseUp(evt) {
    this.__drawIcon = false;
    this.__drawLabel = false;
    this.__activeSprit = null;
    this.__iconData = null;
    if (this.__mode === floorplannerModes.DRAW) {
      let co = evt.data.getLocalPosition(this.__floorplanContainer);
      let cmCo = new Vector2(co.x, co.y);

      if (!this.__drawArrow) {
        cmCo.x = Dimensioning.pixelToCm(cmCo.x);
        cmCo.y = Dimensioning.pixelToCm(cmCo.y);

        if (Configuration.getBooleanValue(snapToGrid) || this.__snapToGrid) {
          cmCo.x =
            Math.round(cmCo.x / Configuration.getNumericValue(snapTolerance)) *
            Configuration.getNumericValue(snapTolerance);
          cmCo.y =
            Math.round(cmCo.y / Configuration.getNumericValue(snapTolerance)) *
            Configuration.getNumericValue(snapTolerance);
        }

        let existingCorners = this.__floorplan.corners.slice(0);
        let existingRooms = this.__floorplan.rooms.slice(0);
        // This creates the corner already
        let corner = this.__floorplan.newCorner(cmCo.x, cmCo.y);

        // further create a newWall based on the newly inserted corners
        // (one in the above line and the other in the previous mouse action
        // of start drawing a new wall)
        if (this.__lastNode != null) {
          this.__floorplan.newWall(this.__lastNode, corner, null, null, true);
          this.__floorplan.newWallsForIntersections(this.__lastNode, corner);
          // this.__tempWall.visible = false;
          // this.switchMode(floorplannerModes.MOVE);
        }
        if (corner.mergeWithIntersected() && this.__lastNode != null) {
          // this.__tempWall.visible = false;
          // this.__lastNode = null;
          // this.switchMode(floorplannerModes.MOVE);
          // let selectedRoomView = this.__getNewRoomData();
          // if(selectedRoomView){
          //     selectedRoomView.selected = true;
          // }
        }

        if (existingRooms.length != this.__floorplan.rooms.length) {
          this.__tempWall.visible = false;
          this.__lastNode = null;
          this.switchMode(floorplannerModes.MOVE);
          let selectedRoomView = this.__getNewRoomData();
          if (selectedRoomView) {
            selectedRoomView.selected = true;
          }
          return;
        }

        if (
          this.__lastNode === null &&
          this.__mode === floorplannerModes.DRAW
        ) {
          this.__tempWall.visible = true;
        }

        if (IS_TOUCH_DEVICE && corner && this.__lastNode !== null) {
          this.__tempWall.visible = false;
          this.__lastNode = null;
        } else {
          this.__lastNode = corner;
        }
      } else {
        let startPoint = new Vector2(cmCo.x, cmCo.y);
        this.__arrowPoints.push(startPoint);
        if (
          this.__lastNode === null &&
          this.__mode === floorplannerModes.DRAW
        ) {
          this.__tempArrow.visible = true;
        }

        if (this.__arrowPoints.length === 2) {
          this.__floorplan.newArrow(this.__lastNode, startPoint);
          this.__tempArrow.visible = false;
          this.__lastNode = null;
          this.__drawArrow = false;
          this.__arrowPoints = [];
          this.__arrowPoints.length = 0;
          this.switchMode(floorplannerModes.MOVE);
          let selectedArrow = this.__arrow2d[this.__arrow2d.length - 1];
          selectedArrow.selected = true;
          return;
        }

        if (IS_TOUCH_DEVICE && startPoint && this.__lastNode !== null) {
          this.__floorplan.newArrow(this.__lastNode, startPoint);
          this.__tempArrow.visible = false;
          this.__lastNode = null;
          this.__drawArrow = false;
          this.__arrowPoints = [];
          this.__arrowPoints.length = 0;
          this.switchMode(floorplannerModes.MOVE);
          let selectedArrow = this.__arrow2d[this.__arrow2d.length - 1];
          selectedArrow.selected = true;
          return;
        } else {
          this.__lastNode = startPoint;
        }
      }
    }
    // else if (this.__mode === floorplannerModes.EDIT_ISLANDS) {
    //   if (this.__currentSelection instanceof Room) {
    //     this.__groupTransformer.visible = false;
    //     this.__groupTransformer.selected = null;
    //     this.__changeCursorMode();
    //     this.switchMode(floorplannerModes.MOVE);
    //   }
    // }
  }

  __drawModeMouseMove(evt) {
    if (this.__mode === floorplannerModes.DRAW) {
      let co = evt.data.getLocalPosition(this.__floorplanContainer);
      let cmCo = new Vector2(co.x, co.y);

      let lastNode = undefined;

      if (!this.__drawArrow) {
        cmCo.x = Dimensioning.pixelToCm(cmCo.x);
        cmCo.y = Dimensioning.pixelToCm(cmCo.y);

        // if (Configuration.getBooleanValue(snapToGrid) || this.__snapToGrid) {
        //     cmCo.x = Math.floor(cmCo.x / Configuration.getNumericValue(snapTolerance)) * Configuration.getNumericValue(snapTolerance);
        //     cmCo.y = Math.floor(cmCo.y / Configuration.getNumericValue(snapTolerance)) * Configuration.getNumericValue(snapTolerance);
        // }
        if (Configuration.getBooleanValue(snapToGrid) || this.__snapToGrid) {
          cmCo.x =
            Math.round(cmCo.x / Configuration.getNumericValue(snapTolerance)) *
            Configuration.getNumericValue(snapTolerance);
          cmCo.y =
            Math.round(cmCo.y / Configuration.getNumericValue(snapTolerance)) *
            Configuration.getNumericValue(snapTolerance);
        }

        if (this.__lastNode !== null) {
          this.__tempWall.update(this.__lastNode, cmCo);
        } else {
          this.__tempWall.update(lastNode, undefined, cmCo);
        }
      } else {
        if (this.__lastNode !== null) {
          this.__tempArrow.update(this.__lastNode, cmCo);
        } else {
          this.__tempArrow.update(lastNode, undefined, cmCo);
        }
      }
    }
    if (this.__mode === floorplannerModes.MOVE) {
      let co = evt.data.getLocalPosition(this.__floorplanContainer);
      let cmCo = new Vector2(co.x, co.y);

      let scope = this;
      let activeData = null;
      if (this.__drawIcon) {
        this.__icon2d.forEach((item) => {
          if (item.icon.id == scope.__iconData.id) {
            activeData = item;
          }
        });
        this.__activeSprit = activeData;
        if (this.__activeSprit) {
          this.__activeSprit.__iconSprite.position = cmCo;
          this.__activeSprit.icon.location = cmCo;
        }
      }

      if (this.__drawLabel) {
        this.__label2d.forEach((item) => {
          if (item.label.id == scope.__iconData.id) {
            activeData = item;
          }
        });
        this.__activeSprit = activeData;
        if (this.__activeSprit) {
          this.__activeSprit.__labelTextField.position = cmCo;
          this.__activeSprit.label.location = cmCo;
        }
      }
    }
  }

  __cornerMoved(evt) {
    if (this.__mode === floorplannerModes.EDIT_ISLANDS) {
      return;
    }
    this.__groupTransformer.visible = false;
    this.__groupTransformer.selected = null;
  }

  __selectionMonitor(evt) {
    this.__currentSelection = null;
    this.__groupTransformer.visible = false;
    this.__groupTransformer.selected = null;
    // this.__eventDispatcher.dispatchEvent({ type: EVENT_NOTHING_SELECTED });
    for (let i = 0; i < this.__entities2D.length; i++) {
      let entity = this.__entities2D[i];
      if (evt.item !== undefined) {
        if (evt.item === entity) {
          continue;
        }
      }
      entity.selected = false;
    }

    if (evt.item) {
      let item = null;
      if (evt.item instanceof WallView) {
        item = evt.item.wall;
        this.__eventDispatcher.dispatchEvent({
          type: EVENT_WALL_CLICKED,
          item: evt.item.wall,
          entity: evt.item,
        });
      } else if (evt.item instanceof CornerView) {
        item = evt.item.corner;
        this.__eventDispatcher.dispatchEvent({
          type: EVENT_CORNER_CLICKED,
          item: evt.item.corner,
          entity: evt.item,
        });
      } else if (evt.item instanceof RoomView) {
        item = evt.item.room;
        this.__eventDispatcher.dispatchEvent({
          type: EVENT_ROOM_CLICKED,
          item: evt.item.room,
          entity: evt.item,
        });
      } else if (evt.item instanceof WallDimensions) {
        item = evt.item;
        this.__eventDispatcher.dispatchEvent({
          type: EVENT_WALL_DIMENSION_CLICKED,
          item: evt.item,
          entity: evt.item,
        });
      } else if (evt.item instanceof RoomDimensions) {
        item = evt.item;
        this.__eventDispatcher.dispatchEvent({
          type: EVENT_ROOM_DIMENSION_CLICKED,
          item: evt.item,
          entity: evt.item,
        });
      } else if (evt.item instanceof Icons) {
        item = evt.item;
        this.__drawIcon = false;
        this.__eventDispatcher.dispatchEvent({
          type: EVENT_ICONS_CLICKED,
          item: evt.item,
          entity: evt.item,
        });
      } else if (evt.item instanceof Labels) {
        item = evt.item;
        // item.__isDragging = false;
        this.__drawLabel = false;
        this.__eventDispatcher.dispatchEvent({
          type: EVENT_LABEL_CLICKED,
          item: evt.item,
          entity: evt.item,
        });
      } else if (evt.item instanceof ArrowView) {
        item = evt.item;
        this.__eventDispatcher.dispatchEvent({
          type: EVENT_ARROW_CLICKED,
          item: evt.item,
          entity: evt.item,
        });
      }

      if (this.__mode === floorplannerModes.EDIT_ISLANDS) {
        this.__groupTransformer.visible = true;
        this.__groupTransformer.selected = item;
      }

      this.__currentSelection = item;
    } else {
      // console.log("nothing selected");
      // console.log(this.__mode);
      this.__currentSelection = null;
      this.__groupTransformer.visible = false;
      this.__groupTransformer.selected = null;
      this.__eventDispatcher.dispatchEvent({ type: EVENT_NOTHING_SELECTED });
    }
  }

  __center() {
    let floorplanCenter = this.__floorplan.getCenter();
    let zoom = this.__floorplanContainer.scale.x;
    let windowSize = new Vector2(this.__currentWidth, this.__currentHeight);
    let bounds =
      Dimensioning.cmToPixel(Configuration.getNumericValue(viewBounds)) * zoom;
    // console.log(windowSize.x, windowSize.y);
    let x = windowSize.x * 0.5 - floorplanCenter.x * 0.5; // - (bounds*0.5);
    let y = windowSize.y * 0.5 - floorplanCenter.z * 0.5; // - (bounds*0.5);
    this.__floorplanContainer.x = x;
    this.__floorplanContainer.y = y;
    this.__tempWallHolder.x = x;
    this.__tempWallHolder.y = y;
  }

  __zoomed() {
    let zoom = this.__floorplanContainer.scale.x;
    let bounds = Dimensioning.cmToPixel(
      Configuration.getNumericValue(viewBounds),
    ); // * zoom;
    let maxZoomOut = Math.max(window.innerWidth, window.innerHeight) / bounds;
    zoom = zoom < maxZoomOut ? maxZoomOut : zoom > 60 ? 60 : zoom;
    let ZoomFont = zoom > 1 ? 1.0 : zoom;
    if (this.__mode == floorplannerModes.DRAW) {
      this.__tempWall.fontSize = fontSizeList[ZoomFont.toFixed(1)] / 2;
    }

    // this.setFontSize(this.__tempWall.fontSize);
    this.__floorplanContainer.scale.x = this.__floorplanContainer.scale.y =
      zoom;
    this.__tempWallHolder.scale.x = this.__tempWallHolder.scale.y = zoom;

    this.__grid2d.gridScale = this.__floorplanContainer.scale.x;
  }

  __panned() {
    let zoom = this.__floorplanContainer.scale.x;
    let bounds =
      Dimensioning.cmToPixel(Configuration.getNumericValue(viewBounds)) * zoom;

    let xy = new Vector2(
      this.__floorplanContainer.x,
      this.__floorplanContainer.y,
    );
    let topleft = new Vector2(-(bounds * 0.5), -(bounds * 0.5));
    let bottomright = new Vector2(bounds * 0.5, bounds * 0.5);

    // let windowSize = new Vector2(window.innerWidth, window.innerHeight);
    let windowSize = new Vector2(this.__currentWidth, this.__currentHeight);

    let xValue = Math.min(-topleft.x, xy.x);
    let yValue = Math.min(-topleft.y, xy.y);

    xValue = Math.max(windowSize.x - bottomright.x, xValue);
    yValue = Math.max(windowSize.y - bottomright.y, yValue);

    this.__floorplanContainer.x = this.__tempWallHolder.x = xValue;
    this.__floorplanContainer.y = this.__tempWallHolder.y = yValue;
  }

  __resetFloorplan(evt) {
    this.__mode = floorplannerModes.MOVE;
    this.__groupTransformer.visible = false;
    this.__groupTransformer.selected = null;
    // this.__redrawFloorplan();
    this.__drawExternalFloorplan();
  }

  __redrawFloorplan() {
    let scope = this;
    let i = 0;

    // clear scene
    scope.__entities2D.forEach((entity) => {
      entity.removeFloorplanListener(
        EVENT_SELECTED,
        this.__selectionMonitorEvent,
      );
      entity.remove();
    });

    this.__floorplanElementsHolder.children.forEach(function (child) {
      child.destroy(true);
    });

    this.__drawBoundary();

    this.__corners2d = [];
    this.__walls2d = [];
    this.__rooms2d = [];
    this.__entities2D = [];
    this.__roomDiemensionField2d = [];
    this.__wallDiemensionField2d = [];
    this.__icon2d = [];
    this.__label2d = [];

    let rooms = this.__floorplan.getRooms();

    for (i = 0; i < rooms.length; i++) {
      let modelRoom = rooms[i];
      let roomView = new RoomView(this.__floorplan, this.__options, modelRoom);
      let roomDimensions = roomView.roomDimensions;

      this.__floorplanElementsHolder.addChild(roomView);
      this.__rooms2d.push(roomView);
      this.__entities2D.push(roomView);
      this.__entities2D.push(roomDimensions);
      this.__roomDiemensionField2d.push(roomDimensions);

      //   roomTransformControl.visible = false;
      //   this.__entities2D.push(roomTransformControl);
      roomView.interactive = this.__mode === floorplannerModes.MOVE;
      roomDimensions.interactive = this.__mode === floorplannerModes.MOVE;

      roomView.addFloorplanListener(
        EVENT_SELECTED,
        this.__selectionMonitorEvent,
      );
      roomDimensions.addFloorplanListener(
        EVENT_SELECTED,
        this.__selectionMonitorEvent,
      );
    }
    for (i = 0; i < this.__floorplan.walls.length; i++) {
      let modelWall = this.__floorplan.walls[i];
      let wallView = new WallView(this.__floorplan, this.__options, modelWall);
      let wallDimensionLabel = wallView.dimensionLabel;
      this.__floorplanElementsHolder.addChild(wallView);
      this.__walls2d.push(wallView);
      this.__entities2D.push(wallView);
      this.__entities2D.push(wallDimensionLabel);
      this.__wallDiemensionField2d.push(wallDimensionLabel);
      wallView.interactive = this.__mode === floorplannerModes.MOVE;
      wallDimensionLabel.interactive = this.__mode === floorplannerModes.MOVE;
      wallDimensionLabel.__textfield.interactive =
        this.__mode === floorplannerModes.MOVE;
      wallView.addFloorplanListener(
        EVENT_SELECTED,
        this.__selectionMonitorEvent,
      );
      wallDimensionLabel.addFloorplanListener(
        EVENT_SELECTED,
        this.__selectionMonitorEvent,
      );
    }
    for (i = 0; i < this.__floorplan.corners.length; i++) {
      let modelCorner = this.__floorplan.corners[i];
      let cornerView = new CornerView(
        this.__floorplan,
        this.__options,
        modelCorner,
      );
      this.__floorplanElementsHolder.addChild(cornerView);
      this.__corners2d.push(cornerView);
      this.__entities2D.push(cornerView);
      cornerView.interactive = this.__mode === floorplannerModes.MOVE;
      cornerView.addFloorplanListener(
        EVENT_SELECTED,
        this.__selectionMonitorEvent,
      );
      modelCorner.removeEventListener(EVENT_MOVED, this.__cornerMovedEvent);
      modelCorner.addEventListener(EVENT_MOVED, this.__cornerMovedEvent);
    }

    for (i = 0; i < this.__floorplan.icons.length; i++) {
      let icon = this.__floorplan.icons[i];
      let iconView = new Icons(this.__floorplan, this.__options, icon);
      this.__floorplanElementsHolder.addChild(iconView);
      this.__entities2D.push(iconView);
      this.__icon2d.push(iconView);
      iconView.interactive = this.__mode === floorplannerModes.MOVE;
      iconView.addFloorplanListener(
        EVENT_SELECTED,
        this.__selectionMonitorEvent,
      );
    }

    for (i = 0; i < this.__floorplan.labels.length; i++) {
      let label = this.__floorplan.labels[i];
      let labelView = new Labels(this.__floorplan, this.__options, label);
      this.__floorplanElementsHolder.addChild(labelView);
      this.__entities2D.push(labelView);
      this.__label2d.push(labelView);
      labelView.interactive = this.__mode === floorplannerModes.MOVE;
      labelView.addFloorplanListener(
        EVENT_SELECTED,
        this.__selectionMonitorEvent,
      );
    }
    for (i = 0; i < this.__floorplan.arrows.length; i++) {
      let arrow = this.__floorplan.arrows[i];
      let arrowView = new ArrowView(this.__floorplan, this.__options, arrow);
      this.__floorplanElementsHolder.addChild(arrowView);
      this.__arrow2d.push(arrowView);
      this.__entities2D.push(arrowView);
      arrowView.interactive = this.__mode === floorplannerModes.MOVE;
      arrowView.addFloorplanListener(
        EVENT_SELECTED,
        this.__selectionMonitorEvent,
      );
    }

    this._handleWindowResize();
  }

  __drawExternalFloorplan() {
    let scope = this;
    let i = 0;
    // clear scene
    scope.__externalEntities2d.forEach((entity) => {
      entity.remove();
    });

    this.__drawBoundary();

    this.__externalCorners2d = [];
    this.__externalWalls2d = [];
    this.__externalRooms2d = [];

    let rooms = this.__floorplan.externalRooms;

    for (i = 0; i < rooms.length; i++) {
      let modelRoom = rooms[i];
      let roomView = new RoomView(this.__floorplan, this.__options, modelRoom);
      this.__floorplanElementsHolder.addChild(roomView);
      this.__externalRooms2d.push(roomView);
      this.__externalEntities2d.push(roomView);
    }
    for (i = 0; i < this.__floorplan.externalWalls.length; i++) {
      let modelWall = this.__floorplan.externalWalls[i];
      let wallView = new WallView(this.__floorplan, this.__options, modelWall);
      this.__floorplanElementsHolder.addChild(wallView);
      this.__externalWalls2d.push(wallView);
      this.__externalEntities2d.push(wallView);
    }
    for (i = 0; i < this.__floorplan.externalCorners.length; i++) {
      let modelCorner = this.__floorplan.externalCorners[i];
      let cornerView = new CornerView(
        this.__floorplan,
        this.__options,
        modelCorner,
      );
      this.__floorplanElementsHolder.addChild(cornerView);
      this.__externalCorners2d.push(cornerView);
      this.__externalEntities2d.push(cornerView);
    }
    this._handleWindowResize();
  }

  /** */
  _handleWindowResize() {
    let heightMargin = this.__canvasHolder.offsetTop;
    let widthMargin = this.__canvasHolder.offsetLeft;

    let w = this.__options.resize
      ? window.innerWidth - widthMargin
      : this.__canvasHolder.clientWidth;
    let h = this.__options.resize
      ? window.innerHeight - heightMargin
      : this.__canvasHolder.clientHeight;

    this.__currentWidth = w;
    this.__currentHeight = h;

    this.renderer.resize(w, h);
    this.renderer.view.style.width = w + "px";
    this.renderer.view.style.height = h + "px";
    this.renderer.view.style.display = "block";
    this.__floorplanContainer.resize(
      w,
      h,
      this.__worldWidth,
      this.__worldHeight,
    );

    this.renderer.render(this.stage);
    this.__zoomed();
    this.__panned();
  }

  addFloorplanListener(type, listener) {
    this.__eventDispatcher.addEventListener(type, listener);
  }

  removeFloorplanListener(type, listener) {
    this.__eventDispatcher.removeEventListener(type, listener);
  }

  dispose() {
    this.__floorplanContainer.off("zoomed", this.__zoomedEvent);
    this.__floorplanContainer.off("moved", this.__pannedEvent);
    this.__floorplanContainer.off("clicked", this.__selectionMonitorEvent);

    // this.__floorplan.addEventListener(EVENT_UPDATED, (evt) => scope.__redrawFloorplan(evt));
    this.__floorplan.removeEventListener(
      EVENT_NEW,
      this.__redrawFloorplanEvent,
    );
    this.__floorplan.removeEventListener(
      EVENT_DELETED,
      this.__redrawFloorplanEvent,
    );
    this.__floorplan.removeEventListener(
      EVENT_LOADED,
      this.__redrawFloorplanEvent,
    );
    window.removeEventListener("resize", this.__windowResizeEvent);
    window.removeEventListener("orientationchange", this.__windowResizeEvent);
  }
}
